'use client';
import React, { FC, memo } from 'react';
import styles from './index.module.scss';
import cls from 'classnames';
import { Button, TextLink, Countdown, getProcessedUrl } from '@bfe/moly';
import { TacticsIcon, ShareIcon, CoinComingIcon } from '@bfe/moly-icons';
import { safeToFormat1 as safeToFormat } from '@/utils/number';
import { useI18n, useUserInfo } from '@spot-fe/base-ui';
import type { OnGoingProject } from '@/apis';
import { ongoingProjectClick, completedProjectClick } from '@/config/sensor';
import { useShare } from '../Referral';
import { useText } from './text';
import { navToLogin } from '@/app/helper';
import { h5Jump, pcJump, uniTryJump } from '@spot-fe/app-bridge';
import { checkAccountToRegister, isApp } from '@spot-fe/utils';
import { getReferralCenterUrl } from '@/utils/links';

export interface Props {
  project: OnGoingProject;
  isPc: boolean;
  onAction?: (info: OnGoingProject) => void;
}

const OnGoingProjectCard: FC<Props> = ({ project, isPc, onAction }) => {
  const {
    totalPrizePool,
    icon,
    depositEnd,
    publishTime,
    participants,
    taskType,
    prizeToken,
    code,
  } = project || {};
  const { t } = useI18n();
  const { isLogin } = useUserInfo();
  const textState = useText(project);
  const { openShareModal } = useShare();

  const isProjectEnd = publishTime < Date.now();
  const isDepositEnd = depositEnd < Date.now();

  const nameDisplay = textState.getNameText();

  const endKey = taskType === 1 ? t('endsIn') : t('tradeTaskEndsIn');
  const countdownDesc = isDepositEnd ? t('announcedIn') : endKey;

  const actionText = textState.getActionText();
  const shareText = textState.getShareText();

  const handleShare = () => {
    if (!isLogin) {
      if (checkAccountToRegister('spotx_tokensplash_ongoing')) {
        return;
      }
      navToLogin();
      return;
    }

    openShareModal({
      prizepool: totalPrizePool,
      token: prizeToken,
      code: code,
    });
  };

  const handleAction = () => {
    if (isProjectEnd) {
      completedProjectClick();
    } else {
      if (!isLogin && checkAccountToRegister('spotx_tokensplash_ongoing')) {
        return;
      }
      ongoingProjectClick();
    }

    onAction?.(project);
  };

  const handleClick = (url) => {
    if (url) {
      if (isApp()) {
        h5Jump(`bybitapp://open/web?url=${encodeURIComponent(url)}`);
        return;
      }
      pcJump(url);
    }
  };

  const navToReferralCenter = () => {
    if (!isLogin) {
      if (checkAccountToRegister('spotx_tokensplash_ongoing')) {
        return;
      }
      navToLogin();
      return;
    }
    const link = getReferralCenterUrl({ source: 2, activityCode: code });
    handleClick(link);
  };

  if (!isPc) {
    return (
      <div className={cls(styles.projectCardH5)}>
        <div className={styles.status}>{t('ongoing')}</div>
        <div className={styles.headerArea}>
          <img className={styles.icon} src={getProcessedUrl(icon, {})} alt="" />
          <div className={styles.body}>
            <div className={styles.tokenName}>{nameDisplay.title}</div>
            <div className={styles.share} onClick={handleShare}>
              <img
                className={styles.shareIcon}
                src="/bycsi-root/bybit/deadpool/image-f2780762094e490292f4467d0698fcf5.svg"
              />
              <div
                className={styles.shareText}
                dangerouslySetInnerHTML={{
                  __html: shareText.tip,
                }}
              />
              {shareText.hasReferral ? <TextLink></TextLink> : null}
            </div>
          </div>
        </div>
        <div className={styles.hr}></div>
        <div className={styles.contentArea}>
          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>
                {t('totalPrizePool')} ({prizeToken})
              </div>
              <div className={styles.infoItemText}>
                {safeToFormat(totalPrizePool)}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{t('participants')}</div>
              <div className={styles.infoItemText}>
                {safeToFormat(participants)}
              </div>
            </div>
          </div>
          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{countdownDesc}</div>
              <div className={styles.infoItemText}>
                <Countdown
                  targetTimestamp={isDepositEnd ? publishTime : depositEnd}
                  theme="light"
                  size="xsmall"
                />
              </div>
            </div>
            <div className={styles.infoItem}>
              <Button variant="primary" size="x-small" onClick={handleAction}>
                {actionText.btn}
              </Button>
            </div>
          </div>
        </div>
        {actionText.tip || nameDisplay.tag ? (
          <>
            <div className={styles.hr}></div>
            <div className={styles.footer}>
              {nameDisplay.tag ? (
                <div className={styles.tag}>{nameDisplay.tag}</div>
              ) : null}
              {actionText.tip ? (
                <div className={styles.tipArea}>
                  <TacticsIcon />
                  {actionText.tip}
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cls(styles.projectCard)}>
      <span className={styles.status}>
        <CoinComingIcon />
        {t('ongoing')}
      </span>
      <div className={styles.contentArea}>
        <div className={styles.brand}>
          <img className={styles.icon} src={getProcessedUrl(icon, {})} alt="" />
        </div>
        <div className={styles.body}>
          <div className={styles.title}>
            <div className={styles.text}>{nameDisplay.title}</div>
            {nameDisplay.tag ? (
              <div className={styles.tag}>{nameDisplay.tag}</div>
            ) : null}
          </div>
          <div className={styles.info}>
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>
                {t('totalPrizePool')} ({prizeToken})
              </div>
              <div className={styles.infoItemText}>
                {safeToFormat(totalPrizePool)}
              </div>
            </div>
            <i className={styles.div} />
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{t('participants')}</div>
              <div className={styles.infoItemText}>
                {safeToFormat(participants)}
              </div>
            </div>
            <i className={styles.div} />
            <div className={styles.infoItem}>
              <div className={styles.infoItemLabel}>{countdownDesc}</div>
              <div className={styles.infoItemCountdown}>
                <Countdown
                  targetTimestamp={isDepositEnd ? publishTime : depositEnd}
                  theme="light"
                  size="small"
                />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.action}>
          <div className={styles.actionTip}>
            {actionText.tip ? (
              <>
                <TacticsIcon />
                {actionText.tip}
              </>
            ) : null}
          </div>
          <div className={styles.actionBtn}>
            <Button variant="primary" size="small" onClick={handleAction}>
              {actionText.btn}
              <i
                className="moly-iconfont bit-a-icon_directionright"
                style={{ paddingLeft: 4 }}
              />
            </Button>
          </div>
        </div>
      </div>
      <div className={styles.shareArea}>
        <div className={styles.share}>
          <img
            className={styles.shareIcon}
            src="/bycsi-root/bybit/deadpool/image-f2780762094e490292f4467d0698fcf5.svg"
          />
          <div className={styles.shareText}>
            <span dangerouslySetInnerHTML={{ __html: shareText.tip }}></span>
            {shareText.hasReferral ? (
              <TextLink
                size="small"
                className={styles.shareRule}
                onClick={navToReferralCenter}
              >
                {t('viewDetails')}
              </TextLink>
            ) : null}
          </div>
          <div
            className={cls(styles.shareBtn, styles.shareEarn)}
            onClick={handleShare}
          >
            {shareText.action}
            <ShareIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(OnGoingProjectCard);
